import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ModuleInfoDto, ModuleLoadReportDto } from '@cpm/shared';
import { AlertTriangle, Blocks, Lock, PackagePlus } from 'lucide-react';
import { api } from '../api/client.js';
import { Badge, Card, ErrorNote, PageHeader, Spinner, cx } from '../components/ui.js';

function Toggle({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        checked ? 'bg-brand-600' : 'bg-slate-300 dark:bg-slate-600',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <span
        className={cx(
          'inline-block size-4.5 transform rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-6' : 'translate-x-1',
        )}
      />
    </button>
  );
}

/** Human-friendly labels for the machine refusal codes (G21, ADR-0035). */
const REFUSAL_LABELS: Record<string, string> = {
  invalid_manifest: 'Invalid manifest',
  invalid_key: 'Invalid key',
  duplicate_key: 'Duplicate key',
  panel_version_incompatible: 'Panel version incompatible',
  sdk_version_incompatible: 'SDK version incompatible',
  missing_requirement: 'Missing requirement',
  load_error: 'Failed to load',
  invalid_export: 'Invalid export',
  key_mismatch: 'Key mismatch',
};

export function ModulesPage() {
  const queryClient = useQueryClient();
  const modules = useQuery({
    queryKey: ['modules'],
    queryFn: () => api.get<ModuleInfoDto[]>('/api/system/modules'),
  });
  const report = useQuery({
    queryKey: ['modules', 'load-report'],
    queryFn: () => api.get<ModuleLoadReportDto>('/api/system/modules/load-report'),
  });

  const toggle = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      api.patch<ModuleInfoDto>(`/api/system/modules/${key}`, { enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['modules'] });
      void queryClient.invalidateQueries({ queryKey: ['system', 'nav'] });
    },
  });

  return (
    <div>
      <PageHeader
        title="Modules"
        description="Every feature area of this panel is a module. New capabilities plug in here."
      />
      {toggle.error != null && (
        <div className="mb-4">
          <ErrorNote error={toggle.error} />
        </div>
      )}

      {modules.isPending ? (
        <Spinner />
      ) : modules.error ? (
        <ErrorNote error={modules.error} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {modules.data.map((module) => (
            <Card key={module.key} className="flex items-start justify-between gap-4 p-5">
              <div className="flex gap-3">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-400">
                  <Blocks className="size-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold">{module.name}</h2>
                    <span className="text-xs text-slate-400">v{module.version}</span>
                    {module.core && (
                      <Badge tone="gray">
                        <Lock className="mr-1 size-3" /> core
                      </Badge>
                    )}
                    {module.source === 'external' && (
                      <Badge tone="blue">
                        <PackagePlus className="mr-1 size-3" /> external
                      </Badge>
                    )}
                  </div>
                  <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{module.description}</p>
                </div>
              </div>
              <Toggle
                checked={module.enabled}
                disabled={module.core || toggle.isPending}
                label={`Toggle ${module.name}`}
                onChange={(enabled) => toggle.mutate({ key: module.key, enabled })}
              />
            </Card>
          ))}
        </div>
      )}

      {/* Out-of-tree module load report (G21, ADR-0035). Only shown once a
          modules directory is configured — otherwise this whole section is
          absent and the panel behaves exactly as before. */}
      {report.data && report.data.modulesDir && (
        <div className="mt-10">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Out-of-tree modules
          </h2>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Loaded from <code className="font-mono">{report.data.modulesDir}</code> at boot.
          </p>

          {report.data.refused.length > 0 && (
            <div className="mt-4 space-y-2">
              {report.data.refused.map((r, i) => (
                <div
                  key={`${r.dir}-${i}`}
                  className="rounded-lg bg-amber-50 px-4 py-3 text-sm ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/10"
                >
                  <div className="flex items-center gap-2 font-medium text-amber-800 dark:text-amber-300">
                    <AlertTriangle className="size-4 shrink-0" />
                    {r.key ?? r.packageName ?? r.dir}
                    <Badge tone="yellow">{REFUSAL_LABELS[r.code] ?? r.code}</Badge>
                  </div>
                  <p className="mt-1 text-amber-700 dark:text-amber-400/90">{r.message}</p>
                </div>
              ))}
            </div>
          )}

          {report.data.loaded.length === 0 && report.data.refused.length === 0 && (
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              No external modules found in the modules directory.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
